import { useTranslation } from "react-i18next";
import * as React from "react";

import Api from "@/api";
import { useMe } from "@/contexts/CurrentCompanyContext";
import { useFormattedTimePreferences } from "@/hooks/useFormattedTimePreferences";
import * as Spaces from "@/models/spaces";
import { useSpaceSearch } from "@/models/spaces";
import { includesId } from "@/routes/paths";
import { useCompanyLoaderData } from "@/routes/useCompanyLoaderData";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { dismissToast, showErrorToast, WorkMapPage } from "turboui";
import { convertToWorkMapItems, useWorkMapItems } from "../../models/workMap";
import { usePaths } from "../../routes/paths";
import { finishFirstItemOnboarding } from "./finishFirstItemOnboarding";
import { shouldShowFirstProjectOnboarding } from "./firstProjectOnboarding";
import { useLoadedData } from "./loader";

export function Page() {
  const { t } = useTranslation();
  const paths = usePaths();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useMe();
  const companyLoaderData = useCompanyLoaderData();

  const { data, creationData } = useLoadedData();
  const { workMap, spacesCount, templates } = data;
  const company = data.company ?? companyLoaderData.company;

  const creationFailed = Boolean(creationData.error);
  const retryCreation = React.useRef(creationData.retry);

  React.useEffect(() => {
    retryCreation.current = creationData.retry;
  }, [creationData.retry]);

  React.useEffect(() => {
    if (!creationFailed) return;

    const id = showErrorToast(t("Couldn't load options for adding goals and projects."), t("Try loading them again."), {
      id: `company-work-map-creation-${company.id}`,
      duration: Infinity,
      action: {
        label: t("Try again"),
        onClick: () => {
          void retryCreation.current();
        },
      },
    });
    return () => dismissToast(id);
  }, [company.id, creationFailed]);

  const title = t("{{company}} Work Map", { company: company.name });

  const canAddItem = !creationData.isLoading && !creationData.error && (spacesCount ?? 0) > 0;
  const ownerIds = companyLoaderData.company.owners?.map((owner) => owner.id) ?? [];
  const firstProjectStateVisible = shouldShowFirstProjectOnboarding({
    isOwner: includesId(ownerIds, me?.id),
    setupCompleted: company.setupCompleted,
    hasWorkItems: workMap.length > 0,
    canAddItem: canAddItem && Boolean(data.company?.generalSpace),
  });
  const [items, addItem] = useWorkMapItems(workMap, {
    projectChampionId: firstProjectStateVisible ? me?.id : undefined,
  });
  const spaceSearch = useSpaceSearch({ accessLevel: "edit_access" });
  const formattedTimePreferences = useFormattedTimePreferences();
  const visibleItems = firstProjectStateVisible ? workMap : items;

  const handleItemCreated = React.useCallback(
    (type: "goal" | "project", id: string) => {
      return finishFirstItemOnboarding({
        invalidateWorkMapCache: () => {
          void queryClient.invalidateQueries({ queryKey: Api.companies.getWorkMapQueryKeyPrefix() });
        },
        navigateToItem: () => navigate(type === "project" ? paths.projectPath(id) : paths.goalPath(id)),
      });
    },
    [navigate, paths, queryClient],
  );

  const projectTemplates = React.useMemo(
    () =>
      templates.map((template) => ({
        id: template.id,
        name: template.name,
        spaceId: template.space.id,
        inactivePeopleSummary: template.inactivePeopleSummary,
        inactiveDiscussionCount: template.inactiveDiscussionCount,
      })),
    [templates],
  );

  const handleCreateProjectTemplate = React.useCallback(
    (spaceId: string) => navigate(paths.newProjectTemplatePath(spaceId)),
    [navigate, paths],
  );

  return (
    <WorkMapPage
      title={title}
      items={convertToWorkMapItems(paths, visibleItems)}
      addItem={addItem}
      spaceSearch={spaceSearch}
      addingEnabled={canAddItem}
      creationLoading={creationData.isLoading}
      creationError={Boolean(creationData.error)}
      addItemDefaultSpace={data.company?.generalSpace && Spaces.parseSpaceForTurboUI(paths, data.company.generalSpace)}
      columnOptions={{ hideProject: true }}
      formattedTimePreferences={formattedTimePreferences}
      emptyStateVariant={firstProjectStateVisible ? "first-project" : "standard"}
      onItemCreated={firstProjectStateVisible ? handleItemCreated : undefined}
      projectTemplates={projectTemplates}
      onCreateProjectTemplate={handleCreateProjectTemplate}
    />
  );
}
